import React, { useState } from 'react';
import TrashF from '@decisiv/iconix/lib/components/Trash';
import { Button, Select, Flex } from '@decisiv/ui-components';

import useAdminResource  from '../hooks/useAdminResource';
import useAdminMutation  from '../hooks/useAdminMutation';
import AdminPanel        from './AdminPanel';
import AdminTable        from './AdminTable';
import AdminConfirmModal from './AdminConfirmModal';
import AdminTypeahead    from './AdminTypeahead';
import { renderCell }    from '../utils/cells';

/**
 * Self-fetching panel that renders a nested association table with optional
 * add-form and per-row remove buttons.
 *
 * panelConfig shape:
 * {
 *   title:    'Permissions',
 *   endpoint: (record) => `/admin_kit/roles/${record.id}/permissions`,
 *   columns:  [{ name, title, type? }],
 *
 *   add?: {
 *     endpoint:  (record) => `/admin_kit/roles/${record.id}/add_permission`,
 *     paramKey:  'rbac_role_permission',
 *     field: {
 *       name:            'permission_id',
 *       label:           'Permission',
 *       optionsEndpoint: '/admin_kit/permissions',
 *     },
 *   },
 *
 *   remove?: {
 *     endpoint: (record, row) =>
 *       `/admin_kit/roles/${record.id}/remove_permission/${row.id}`,
 *   },
 * }
 */
const AssocPanel = ({ panelConfig, record, canModify = false }) => {
  const [addValue, setAddValue]         = useState('');
  const [addDisplay, setAddDisplay]     = useState('');
  const [addOptions, setAddOptions]     = useState([]);
  const [optionsError, setOptionsError] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const dataEndpoint = panelConfig.endpoint(record);
  const { records, isLoading, refetch } = useAdminResource(dataEndpoint);

  const { mutate: add, isSubmitting: isAdding } = useAdminMutation({
    onSuccess: () => { setAddValue(''); setAddDisplay(''); refetch(); },
  });

  const { mutate: remove, isSubmitting: isRemoving } = useAdminMutation({
    onSuccess: () => { setRemoveTarget(null); refetch(); },
  });

  // Lazy-load add-form select options on first focus
  const loadOptions = async () => {
    if (!panelConfig.add || addOptions.length || optionsError) return;
    try {
      const res  = await window.fetch(`${panelConfig.add.field.optionsEndpoint}.json`);
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setAddOptions(
        data.records.map((r) => ({ id: String(r.id), label: r.name ?? String(r.id), value: String(r.id) }))
      );
    } catch (_) {
      setOptionsError(true);
    }
  };

  const tableColumns = [
    ...panelConfig.columns.map((col) => ({
      name:     col.name,
      title:    col.title,
      DataCell: ({ rowData }) => renderCell(col, rowData, dataEndpoint),
    })),
    ...(panelConfig.remove && canModify
      ? [{
          name:     '_remove',
          title:    '',
          DataCell: ({ rowData }) => (
            <Button
              icon={TrashF}
              aria-label="Remove"
              size="small"
              kind="secondary"
              intent="danger"
              onClick={() => setRemoveTarget(rowData)}
            />
          ),
        }]
      : []),
  ];

  const isTypeahead = panelConfig.add?.field?.type === 'typeahead';

  const addForm = panelConfig.add && canModify && (
    <Flex gap={2} alignItems="flex-end">
      {isTypeahead ? (
        <AdminTypeahead
          label={panelConfig.add.field.label}
          searchEndpoint={panelConfig.add.field.searchEndpoint}
          value={addValue}
          displayValue={addDisplay}
          onChange={(value, display) => { setAddValue(value); setAddDisplay(display); }}
          helpMessage={null}
        />
      ) : optionsError ? (
        <div style={{ fontSize: 12, color: '#c0392b', alignSelf: 'center' }}>
          Failed to load options — please refresh.
        </div>
      ) : (
        <Select
          label={panelConfig.add.field.label}
          value={addValue}
          onChange={setAddValue}
          onFocus={loadOptions}
          options={[{ id: '', label: 'Select…', value: '' }, ...addOptions]}
        />
      )}
      <Button
        text="Add"
        kind="primary"
        style={{ marginLeft: 12 }}
        disabled={!addValue || isAdding}
        onClick={() =>
          add(panelConfig.add.endpoint(record), {
            body: {
              [panelConfig.add.paramKey]: {
                [panelConfig.add.field.name]: addValue,
              },
            },
          })
        }
      />
    </Flex>
  );

  return (
    <>
      <AdminPanel title={panelConfig.title}>
        {addForm && <Flex marginBottom={2}>{addForm}</Flex>}
        <AdminTable
          columns={tableColumns}
          data={records}
          isLoading={isLoading}
          emptyText="None assigned."
        />
      </AdminPanel>

      {panelConfig.remove && (
        <AdminConfirmModal
          visible={!!removeTarget}
          title={`Remove from ${panelConfig.title}`}
          body={`Remove "${removeTarget?.name ?? removeTarget?.id}"?`}
          confirmText="Remove"
          confirmIntent="danger"
          isSubmitting={isRemoving}
          onConfirm={() =>
            remove(panelConfig.remove.endpoint(record, removeTarget), {
              method: 'DELETE',
            })
          }
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </>
  );
};

export default AssocPanel;
